import { getDistanceUnitSymbolText } from "@/components/common/settings-context/distance-unit-utils";
import { useDistanceUnit } from "@/components/common/settings-context/settings-context";
import { DistanceInputField } from "@/components/ui/distance-input-field";
import {
  selectControlPointsAlignmentAnchorPositions,
  selectControlPointsSheetElevation,
} from "@/store/modes/control-points-alignment-mode-selectors";
import {
  setControlPointsReferencePoint1,
  setControlPointsReferencePoint2,
  setControlPointsSheetElevation,
} from "@/store/modes/control-points-alignment-mode-slice";
import { useAppDispatch, useAppSelector } from "@/store/store-hooks";
import { FaroText, TruncatedFaroText } from "@faro-lotv/flat-ui";
import type { DistanceUnitTypes } from "@faro-lotv/foundation";
import { Stack } from "@mui/material";
import { useCallback, useMemo, useRef, type JSX } from "react";
import { Vector3Tuple } from "three";
import { TwoPointsPairsAlignmentAnchorPositions } from "../alignment-modes-commons/compute-split-screen-alignment";

type ControlPointsAlignmentSetPointsPanelProps = {
  /** true if aligned element is area, false if it is a sheet */
  isArea: boolean;
};

/**
 * @returns true if the input parameters are valid for control points alignment
 * @param alignmentAnchorPositions TwoPointsPairsAlignmentAnchorPositions describing the reference and moving points
 * @param sheetElevation sheet elevation in meter
 */
export function areControlPointsParametersValid(
  alignmentAnchorPositions: TwoPointsPairsAlignmentAnchorPositions,
  sheetElevation?: number,
): boolean {
  return (
    !!alignmentAnchorPositions.movingElementAnchor1 &&
    !!alignmentAnchorPositions.movingElementAnchor2 &&
    !!alignmentAnchorPositions.referenceElementAnchor1 &&
    !!alignmentAnchorPositions.referenceElementAnchor2 &&
    !areTwoPointsForAlignmentTooClose(
      alignmentAnchorPositions.movingElementAnchor1,
      alignmentAnchorPositions.movingElementAnchor2,
    ) &&
    !areTwoPointsForAlignmentTooClose(
      alignmentAnchorPositions.referenceElementAnchor1,
      alignmentAnchorPositions.referenceElementAnchor2,
    ) &&
    sheetElevation !== undefined
  );
}

/**
 * validate if 2 points have distance sufficient to compute alignment
 *
 * @param p1 point1 coordinates
 * @param p2 point2 coordinates
 * @returns true if case if points are too close. In case if one of both inputs are undefined returns false
 */
export function areTwoPointsForAlignmentTooClose(
  p1?: Vector3Tuple,
  p2?: Vector3Tuple,
): boolean {
  if (!p1 || !p2) return false;

  // Minimal allowed distance in meters
  const minAllowedDistance = 0.01;

  const dx = p1[0] - p2[0];
  const dy = p1[2] - p2[2];
  return Math.sqrt(dx * dx + dy * dy) < minAllowedDistance;
}

const errorMEssageForMissingPin = "Missing corresponding control point marker";

/**
 * @returns panel to set control points and elevation
 */
export function ControlPointsAlignmentSetPointsPanel({
  isArea,
}: ControlPointsAlignmentSetPointsPanelProps): JSX.Element {
  const dispatch = useAppDispatch();

  const elevation = useAppSelector(selectControlPointsSheetElevation) ?? 0;

  const alignmentAnchorPositions = useAppSelector(
    selectControlPointsAlignmentAnchorPositions,
  );

  const validationErrorTextPoint1 = useMemo(() => {
    if (
      alignmentAnchorPositions.referenceElementAnchor1 !== undefined &&
      !alignmentAnchorPositions.movingElementAnchor1
    ) {
      return errorMEssageForMissingPin;
    }
  }, [
    alignmentAnchorPositions.referenceElementAnchor1,
    alignmentAnchorPositions.movingElementAnchor1,
  ]);

  const validationErrorTextPoint2 = useMemo(() => {
    const arePointsTooClose = areTwoPointsForAlignmentTooClose(
      alignmentAnchorPositions.referenceElementAnchor1,
      alignmentAnchorPositions.referenceElementAnchor2,
    );
    if (arePointsTooClose) return "Points are too close";
    if (
      alignmentAnchorPositions.referenceElementAnchor2 !== undefined &&
      !alignmentAnchorPositions.movingElementAnchor2
    ) {
      return errorMEssageForMissingPin;
    }
  }, [
    alignmentAnchorPositions.referenceElementAnchor1,
    alignmentAnchorPositions.referenceElementAnchor2,
    alignmentAnchorPositions.movingElementAnchor2,
  ]);

  const distanceUnit = useDistanceUnit();
  const unitSymbol = useMemo(
    () => `\u00A0(${getDistanceUnitSymbolText(distanceUnit)})`,
    [distanceUnit],
  );

  return (
    <Stack
      spacing={3}
      sx={{
        mt: "5px",
        ml: "10px",
        mr: "10px",
        width: 320,
      }}
    >
      <TruncatedFaroText
        variant="bodyM"
        sx={{
          pt: "15px",
          fontSize: "18px",
          fontWeight: 600,
        }}
      >
        Control points
      </TruncatedFaroText>

      <FaroText
        variant="bodyM"
        sx={{
          fontSize: "12px",
        }}
      >
        Add 2 control points and specify their coordinates
      </FaroText>

      <Stack spacing={1}>
        <FaroText
          variant="bodyM"
          sx={{
            fontSize: "12px",
            fontWeight: 600,
          }}
        >
          {`Elevation${unitSymbol}`}
        </FaroText>

        <DistanceInputField
          fullWidth
          disabled={!isArea}
          sx={{ height: "35px" }}
          distanceInMeter={elevation}
          errorMessage="Elevation must be a number"
          distanceUnit={distanceUnit}
          onDistanceChanged={(newValue) => {
            if (newValue !== undefined) {
              dispatch(setControlPointsSheetElevation(newValue));
            }
          }}
        />
      </Stack>

      <TwoCoordinatesControl
        title={`Point 1 coordinates${unitSymbol}`}
        changeReferencePoint={(position) =>
          dispatch(setControlPointsReferencePoint1(position))
        }
        validationErrorText={validationErrorTextPoint1}
        distanceUnit={distanceUnit}
      />
      <TwoCoordinatesControl
        title={`Point 2 coordinates${unitSymbol}`}
        changeReferencePoint={(position) =>
          dispatch(setControlPointsReferencePoint2(position))
        }
        validationErrorText={validationErrorTextPoint2}
        distanceUnit={distanceUnit}
      />
    </Stack>
  );
}

type CoordinateEditControlProps = {
  /** label (x,y,z) to show left to edit control */
  label: string;

  /** coordinate value */
  value: number | undefined;

  /** distance unit to use for display and input */
  distanceUnit: DistanceUnitTypes;

  /** method to send updated coordinate value to parent component */
  setNewValue(value?: number): void;

  /** Optional error text with error of both points validation, appearing only under "Y" of second point */
  validationErrorText?: string;
};

function CoordinateEditControl({
  label,
  value,
  setNewValue,
  validationErrorText,
  distanceUnit,
}: CoordinateEditControlProps): JSX.Element {
  return (
    <Stack direction="column">
      <Stack direction="row" spacing={1} sx={{ alignContent: "center" }}>
        <FaroText
          variant="bodyM"
          sx={{
            alignContent: "center",
            fontSize: "12px",
          }}
        >
          {label}
        </FaroText>

        <DistanceInputField
          fullWidth
          sx={{ height: "35px" }}
          distanceInMeter={value}
          distanceUnit={distanceUnit}
          errorMessage="Coordinate must be a number"
          customValidationError={validationErrorText}
          onDistanceChanged={(newValue) => setNewValue(newValue)}
        />
      </Stack>
    </Stack>
  );
}

type TwoCoordinatesControlProps = {
  /** text of title above two coordinates edit control */
  title: string;

  /** method to send updated reference point coordinates to parent component */
  changeReferencePoint(position?: Vector3Tuple): void;

  /**
   * Optional error text appearing under "Y".
   */
  validationErrorText?: string;

  /** distance unit to use for display and input */
  distanceUnit: DistanceUnitTypes;
};

function TwoCoordinatesControl({
  title,
  changeReferencePoint,
  validationErrorText,
  distanceUnit,
}: TwoCoordinatesControlProps): JSX.Element {
  const pointCoordinateX = useRef<number | undefined>(undefined);
  const pointCoordinateY = useRef<number | undefined>(undefined);

  const handleChangeX = useCallback(
    (x: number | undefined) => {
      pointCoordinateX.current = x;
      // coordinate Z is inverted here  because following computation is done and used for coordinates picked in three.js
      // and expecting Z to be inverted.
      changeReferencePoint(
        x !== undefined && pointCoordinateY.current !== undefined
          ? [x, 0, -pointCoordinateY.current]
          : undefined,
      );
    },
    [changeReferencePoint, pointCoordinateY],
  );

  const handleChangeY = useCallback(
    (y: number | undefined) => {
      pointCoordinateY.current = y;
      changeReferencePoint(
        y !== undefined && pointCoordinateX.current !== undefined
          ? [pointCoordinateX.current, 0, -y]
          : undefined,
      );
    },
    [changeReferencePoint, pointCoordinateX],
  );

  return (
    <Stack spacing={1}>
      <FaroText
        variant="bodyM"
        sx={{
          fontSize: "12px",
          fontWeight: 600,
        }}
      >
        {title}
      </FaroText>

      <CoordinateEditControl
        label="X"
        setNewValue={handleChangeX}
        value={pointCoordinateX.current}
        distanceUnit={distanceUnit}
      />

      <CoordinateEditControl
        label="Y"
        setNewValue={handleChangeY}
        value={pointCoordinateY.current}
        validationErrorText={validationErrorText}
        distanceUnit={distanceUnit}
      />
    </Stack>
  );
}
